// ──────────────────────────────────────────────────────────────
//  src/utils/edt.js
//
//  EDT / EST utilities for i-Panel.
//
//  THE PANEL OPERATES IN AMERICA/NEW_YORK TIME, EVERYWHERE, ALWAYS.
//  No matter where the browser user is physically located, every
//  date the panel displays or sends to the backend is an EDT (or EST
//  in winter) date.
//
//  ⚠  NEVER use the following in page code, they all return the
//      browser's LOCAL time, which is IST for you:
//
//      new Date().toISOString().split("T")[0]   ❌ returns UTC date
//      new Date().getDate()                     ❌ local day
//      new Date().getHours()                    ❌ local hour
//      new Date().toLocaleDateString("en-IN")   ❌ IST display
//
//      Use the helpers below instead.
// ──────────────────────────────────────────────────────────────

export const TZ = "America/New_York";

/**
 * Format a JS Date instant as "YYYY-MM-DD" in EDT.
 * This is the string format we send to the backend and store in MySQL.
 */
export function fmtYMD(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year:  "numeric",
    month: "2-digit",
    day:   "2-digit",
  }).format(date);
}

/**
 * Today's calendar date in EDT, as "YYYY-MM-DD".
 * Use this INSTEAD of new Date().toISOString().split("T")[0].
 */
export function todayYMD() {
  return fmtYMD(new Date());
}

/**
 * Yesterday's calendar date in EDT, as "YYYY-MM-DD".
 */
export function yesterdayYMD() {
  return addDaysYMD(todayYMD(), -1);
}

/**
 * Current EDT calendar parts as plain numbers:
 *   { year, month (1-12), day, hour (0-23), minute, dow (0=Sun..6=Sat) }
 * Use this when you need "what hour is it in EDT" or "what day of week".
 */
export function edtParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year:    "numeric",
    month:   "2-digit",
    day:     "2-digit",
    hour:    "2-digit",
    minute:  "2-digit",
    weekday: "short",
    hour12:  false,
  }).formatToParts(date);
  const p = {};
  for (const part of parts) p[part.type] = part.value;
  const DOW = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
  return {
    year:   parseInt(p.year, 10),
    month:  parseInt(p.month, 10),   // 1-12
    day:    parseInt(p.day, 10),
    // Intl can return "24" for midnight in some environments; normalize to 0
    hour:   parseInt(p.hour === "24" ? "0" : p.hour, 10),
    minute: parseInt(p.minute, 10),
    dow:    DOW[p.weekday] ?? 0,
  };
}

/**
 * Add n days to a YMD string, returning a new YMD string.
 * Negative n goes backwards. Uses UTC math so timezone can never interfere.
 *   addDaysYMD("2026-04-10",  1) => "2026-04-11"
 *   addDaysYMD("2026-04-10", -7) => "2026-04-03"
 */
export function addDaysYMD(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + n);
  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utc.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * First day of a given month, as YMD.
 *   monthStartYMD(2026, 4) => "2026-04-01"
 */
export function monthStartYMD(year, month1to12) {
  return `${year}-${String(month1to12).padStart(2, "0")}-01`;
}

/**
 * Last day of a given month, as YMD.
 *   monthEndYMD(2026, 4) => "2026-04-30"
 */
export function monthEndYMD(year, month1to12) {
  // Day 0 of the NEXT month = last day of THIS month.
  // Pure integer math — no timezone interference.
  const lastDay = new Date(year, month1to12, 0).getDate();
  return `${year}-${String(month1to12).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
}

/**
 * Days in a given (year, zero-indexed month). Used by calendar grid.
 */
export function daysInMonth(year, monthZero) {
  return new Date(year, monthZero + 1, 0).getDate();
}

/**
 * Weekday of the 1st of a given (year, zero-indexed month), Monday-based.
 *   Mon=0, Tue=1, ..., Sun=6. Used by calendar grid.
 */
export function firstWeekdayOfMonth(year, monthZero) {
  const d = new Date(year, monthZero, 1).getDay();   // Sun=0..Sat=6
  return d === 0 ? 6 : d - 1;
}

/**
 * Format a YMD string as display text. Never round-trips through a
 * timezone-affected Date constructor, so it can't off-by-one.
 *   ymdToDisplay("2026-04-10") => "Apr 10, 2026"
 */
export function ymdToDisplay(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/**
 * Full weekday display from a YMD string.
 *   ymdToDisplayFull("2026-04-18") => "Sat, Apr 18, 2026"
 */
export function ymdToDisplayFull(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  // Use UTC so local TZ can't flip the weekday
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${DAYS[dow]}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

/**
 * Just the time in EDT: "10:45 AM"
 * Used in "last synced" labels.
 */
export function edtTime(date = new Date()) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour:   "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/**
 * EDT date + time + zone label: "Sat, Apr 18, 2026 • 10:45 AM EDT"
 * Used in the top header. Automatically switches EDT↔EST in winter.
 */
export function edtHeaderStamp(date = new Date()) {
  const datePart = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    weekday: "short",
    month:   "short",
    day:     "numeric",
    year:    "numeric",
  }).format(date);
  const timePart = edtTime(date);
  const tzShort = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    timeZoneName: "short",
  }).formatToParts(date).find(p => p.type === "timeZoneName")?.value || "EDT";
  return `${datePart} • ${timePart} ${tzShort}`;
}

/**
 * Full datetime display for "last_login", "created_at", etc. from DB.
 *   edtDateTime(new Date(row.last_login)) => "Apr 18, 2026, 10:45 AM"
 */
export function edtDateTime(dateOrStr) {
  if (!dateOrStr) return "—";
  const d = dateOrStr instanceof Date ? dateOrStr : new Date(dateOrStr);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    month:  "short",
    day:    "2-digit",
    year:   "numeric",
    hour:   "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/**
 * Short EDT month label like "Apr 2026" — useful for chart axis titles
 * when we need the CURRENT EDT month (not IST).
 */
export function edtMonthLabel(date = new Date()) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    month: "short",
    year:  "numeric",
  }).format(date);
}

/**
 * Short EDT month name only, "Apr".
 */
export function edtMonthShort(date = new Date()) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    month: "short",
  }).format(date);
}
